import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { TWButton } from '../../../../components/ui';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import connectApi from '../../../../services/connectApi';
import { groupRequirements } from '../../../connect/connect-requirements';
import { apiMessage } from '../../../connect/connect-api-message';
import ConnectActivation from './connect-activation';
import ConnectTerminals from './connect-terminals';
import ConnectSettlement from './connect-settlement';
import ConnectPayoutSchedule from './connect-payout-schedule';
import ConnectSupport from './connect-support-block';
import ConnectSupportBoundary from './connect-support-boundary';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Two independent facts, never one badge. `connectStatus` is how far Stripe has
// got with this restaurant; `paymentModel` is where money actually routes
// today. A company can be READY here and still charge through Thisway's own
// platform account, because routing only moves at Admin Activate (SC-R5).
//
// Both lookup maps are prototype-less. `connectStatus` and `paymentModel` are
// external strings, and a plain object literal answers `constructor` or
// `toString` with an inherited value: the tone object becomes a function
// whose `.label` is undefined. Object.create(null) makes every unknown key
// fall through to the fallback, which is the whole point of having one.
const STATUS_TONES = Object.assign(Object.create(null), {
  NOT_STARTED: {
    label: 'Not started',
    dot: 'bg-slate-400 dark:bg-slate-500',
    text: 'text-slate-700 dark:text-slate-200',
    description: 'No Stripe account has been created for this company yet',
  },
  ONBOARDING: {
    label: 'Onboarding',
    dot: 'bg-blue-500 dark:bg-sky-400',
    text: 'text-blue-700 dark:text-sky-300',
    description:
      'The restaurant has started verification but has not finished it',
  },
  PENDING: {
    label: 'Pending',
    dot: 'bg-amber-500 dark:bg-amber-400',
    text: 'text-amber-700 dark:text-amber-300',
    description: 'Stripe is reviewing what the restaurant submitted',
  },
  RESTRICTED: {
    label: 'Restricted',
    dot: 'bg-red-500 dark:bg-red-400',
    text: 'text-red-700 dark:text-red-400',
    description: 'Stripe needs something more before this account can be used',
  },
  READY: {
    label: 'Ready',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
    text: 'text-emerald-700 dark:text-emerald-300',
    description: 'Stripe has everything it needs from this restaurant',
  },
});

// Not a Stripe status. Covers two cases Stripe never sends: the read itself
// failed, so nothing below is known (the loadError render below supplies its
// own description text and ignores this one); and toneFor()'s own fallback
// for a connectStatus value that came back but is not one of the five this
// screen maps. The label is deliberately not "Not started" — that is a
// specific, false claim about an account that may well exist and be verified
// — and the dot is deliberately not one of the five status colours above, so
// it can never be mistaken for one of them.
const UNKNOWN_TONE = {
  label: 'Unknown',
  dot: 'bg-slate-300 dark:bg-slate-600',
  text: 'text-slate-700 dark:text-slate-200',
  description: "Stripe returned a status this screen doesn't recognise yet",
};
const UNKNOWN_ROUTING_LABEL = 'Unknown';
const UNKNOWN_ROUTING_DESCRIPTION =
  'Not known until the Stripe status can be read';
// Same shape ROUTING's own entries have, so routingFor's fallback below can
// return it interchangeably with a real routing entry.
const UNKNOWN_ROUTING = {
  label: UNKNOWN_ROUTING_LABEL,
  description: UNKNOWN_ROUTING_DESCRIPTION,
};

const ROUTING = Object.assign(Object.create(null), {
  LEGACY_PLATFORM: {
    label: 'Legacy',
    description: "Charges settle on Thisway's account",
  },
  CONNECT_DIRECT: {
    label: 'Direct',
    description: "Charges settle on the restaurant's own Stripe account",
  },
  // A company born Pending (D5/D7) — takes no card payments at all until an
  // admin clicks Activate. Falling through to UNKNOWN_ROUTING for this value
  // (the pre-fix behaviour) mislabels every Pending company "Unknown" on the
  // axes line and renders "Unknown → Direct" in its own activation history.
  CONNECT_PENDING: {
    label: 'Pending',
    description: 'No card payments until an admin activates direct charges',
  },
});

// A direct map hit is the only way to reach STATUS_TONES.NOT_STARTED here —
// including for the literal 'NOT_STARTED' string, which is simply the map's
// own key. Anything else unmapped (an unrecognised value like 'DISABLED', or
// a falsy one) falls to UNKNOWN_TONE, never to NOT_STARTED: "Not started" is
// a specific, false claim about an account that may already exist and be
// verified — see UNKNOWN_TONE's own comment above.
const toneFor = (connectStatus) => STATUS_TONES[connectStatus] || UNKNOWN_TONE;

// An absent CompanyPaymentProfile row means legacy — but the adapter already
// carries that fact: the DTO always sends paymentModel as an int, defaulted to
// 0 (MODEL_BY_CODE[0] === 'LEGACY_PLATFORM' in connectApi.js), so a real
// status object handed to this function never itself has an "absent"
// paymentModel. This fallback fires only for a value the screen genuinely
// does not recognise, and asserting "Legacy" for it is a claim about where
// money routes that nothing here actually knows — the same call toneFor's
// UNKNOWN_TONE already makes for connectStatus. Falls through to the same
// unknown-routing copy the loadError render uses instead.
const routingFor = (paymentModel) => ROUTING[paymentModel] || UNKNOWN_ROUTING;

// The activation history section below reads two more enum vocabularies:
// `entry.action` (connectApi.js's ACTION_BY_CODE) and the same `paymentModel`
// tokens `routingFor` above already humanises for the axes line — reused here
// rather than duplicated, so "Legacy"/"Direct" never drift into two different
// spellings on the same screen. `entry.action` gets its own map: this section
// already has to suppress `admin_activate`/`start_onboarding` for being
// machine codes (see the render below), and rendering `ACTIVATE` or
// `LEGACY_PLATFORM` right next to that suppression would be the same defect
// with a different name. An unmapped code — connectApi.js's ACTION_BY_CODE
// has no entry for it, same as an unrecognised connectStatus or paymentModel
// — resolves to undefined and must not render as a blank cell.
const ACTIVATION_ACTION_LABELS = Object.assign(Object.create(null), {
  START_ONBOARDING: 'Started onboarding',
  ACTIVATE: 'Activated direct charges',
  DEACTIVATE: 'Deactivated direct charges',
  ACCOUNT_RECREATED: 'Re-created the Stripe account (fee payer migration)',
});
const humanizeActivationAction = (action) =>
  ACTIVATION_ACTION_LABELS[action] || 'Unknown action';

// `apiMessage` used to be defined here and exported so
// connect-support-block.js could reuse it, which made the two files import
// each other. Moved to its own module (connect-api-message.js) — see that
// file's own header comment — so this tab and its child can both import it
// without importing each other. Behaviour is unchanged.

// Deliberately a local <p>, not `<FieldLabel variant="micro">` from
// components/ui — whose class string is character for character the same one.
// FieldLabel renders a <label>, and a <label> with no htmlFor and no control
// beside it is a dangling label that tells a screen reader nothing. Same
// pixels, better semantics: do not "de-duplicate" this into the shared
// component.
const Label = ({ children }) => (
  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
    {children}
  </p>
);

// The two-axis header, shared between the normal render and the "couldn't
// read the status" render so the two can never drift into different DOM
// shapes. `<dt>/<dd>` (matching the identity footer's existing pattern)
// associates each label with its value for assistive tech — three sibling
// elements with no relation told a screen reader nothing.
const AxisFacts = ({
  accountDotClass,
  accountTextClass,
  accountLabel,
  accountDescription,
  routingLabel,
  routingDescription,
}) => (
  <dl className="grid gap-6 sm:grid-cols-2">
    <div>
      <dt>
        <Label>Stripe account</Label>
      </dt>
      <dd>
        <div className="mt-2 flex items-center gap-2">
          <span
            className={classNames('h-2.5 w-2.5 rounded-full', accountDotClass)}
            aria-hidden
          />
          <span
            className={classNames('text-base font-semibold', accountTextClass)}
          >
            {accountLabel}
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {accountDescription}
        </p>
      </dd>
    </div>

    {/* Deliberately colourless: "Legacy" is the correct, unalarming state of
        every company today. A second badge here would read as a second
        alarm. */}
    <div>
      <dt>
        <Label>Payment routing</Label>
      </dt>
      <dd>
        <p className="mt-2 text-base font-semibold text-slate-900 dark:text-slate-100">
          {routingLabel}
        </p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {routingDescription}
        </p>
      </dd>
    </div>
  </dl>
);

// `onStatusChange` (optional; spec 2026-10-20 §3, pre-flight I1): the company
// page reads Connect/Status once for its routing badge and its Account
// Activity tab, and this tab is the one place that changes routing — so every
// status it applies (its own first read, Refresh, Re-create, the
// already_legacy re-read, and the patched object after Activate or
// Deactivate) is reported up, and the page follows without a reload.
const ConnectTab = ({ companyDetail, setNotf, onStatusChange }) => {
  const companyId = companyDetail?.id;

  const [status, setStatus] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  // '' when the last load succeeded (or none has finished yet); otherwise the
  // sentence to show in place of the two-axis panel and the actions. A failed
  // read must never fall back to rendering NOT_STARTED's "Not started" / "no
  // account yet" copy — that is a specific, false claim about an account that
  // may already be verified, sitting above a Start button that would then
  // POST a second Connect account into existence.
  const [loadError, setLoadError] = useState('');
  // The Account Link the last Copy link minted, or the one StartOnboarding's
  // own response carried. It lives in state, not in a local variable, because
  // a clipboard rejection must not destroy a link Stripe has already minted
  // and consumed.
  const [linkUrl, setLinkUrl] = useState('');
  // '' | 'start' | 'copy' | 'refresh' | 'activate' | 'deactivate' — one action
  // at a time, and every button is disabled while any of them is in flight.
  const [busy, setBusy] = useState('');

  // SC-R5's readiness/terminals/activation-log state. Independent of `status`:
  // readiness can fail to load while the account status itself is fine (and
  // vice versa), so each gets its own error/loading story rather than sharing
  // `loadError` above.
  const [readiness, setReadiness] = useState(null);
  const [readinessError, setReadinessError] = useState('');
  // False until the first getReadiness attempt (success OR failure) has
  // settled. Before that, `readiness` is null and `readinessError` is '' —
  // the exact same shape a genuinely-empty, ready:false response would have
  // — so ConnectActivation cannot tell "nothing is known yet" from "nothing
  // is wrong" without this. Never reset back to false; a later reload just
  // updates `readiness`/`readinessError` in place.
  const [readinessLoaded, setReadinessLoaded] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [terminalErrors, setTerminalErrors] = useState({});
  // Same idea as `readinessLoaded`, for the terminals read: false until the
  // first getTerminals attempt has settled, so ConnectTerminals can tell "not
  // read yet" from "confirmed zero terminals" instead of asserting the empty
  // state before the read has even happened.
  const [terminalsLoaded, setTerminalsLoaded] = useState(false);
  // '' when the last terminals read succeeded (or none has finished yet);
  // otherwise the sentence to show instead of trusting `terminals` — which is
  // still whatever it was before the failed read, not cleared to []. Cleared
  // to [] on failure, ConnectTerminals would render its own "this company has
  // no terminals" sentence: a false claim that can contradict a `terminal`
  // readiness blocker sitting right above it.
  const [terminalsError, setTerminalsError] = useState('');
  const [activationLog, setActivationLog] = useState([]);
  // Same idea as `terminalsError`: without it, a failed read just makes the
  // whole Activation history section disappear with no explanation.
  const [activationLogError, setActivationLogError] = useState('');
  const [busyTerminalId, setBusyTerminalId] = useState('');

  const notify = useCallback(
    (type, message) => {
      setNotf((prev) => ({ ...prev, display: true, type, message }));
    },
    [setNotf],
  );

  const notifyError = useCallback(
    (error, fallback) => {
      notify('error', apiMessage(error) || fallback);
    },
    [notify],
  );

  // Applies a freshly-read ConnectStatus and drops any Account Link that was
  // minted for the *previous* status. Without this, a Refresh (or a second
  // Start) leaves a stale link on screen next to facts that have since moved
  // on, or next to an error toast that has nothing to do with the link at
  // all.
  //
  // `next` is null whenever `connectApi.toConnectStatus` could not read a
  // status object out of the response — GetStatus, StartOnboarding and
  // Refresh all answered 2xx, but not with something this screen can use.
  // Writing that through would blank `status` to null while `loadError`
  // stayed '', and the render reads that as `hasAccount === false`: "no
  // Connect account yet" above an *enabled* Start onboarding button, for a
  // company the call never confirmed has no account. Refusing the write here
  // rather than at each call site keeps all three readers honest; the caller
  // is told so it can report the failure the same way it reports a thrown
  // one.
  const applyStatus = useCallback((next) => {
    if (!next) return false;
    setStatus(next);
    setLinkUrl('');
    if (onStatusChange) onStatusChange(next);
    return true;
  }, [onStatusChange]);

  // StartOnboarding answered 2xx, so §3 says an account now exists whether or
  // not the re-read behind it told us anything. Keep that one fact and mark
  // everything the re-read failed to deliver as unknown. Without this the tab
  // holds the pre-start payload and goes on saying "no Connect account yet"
  // above an enabled Start button, inviting a second POST against the account
  // that call just created. `hasAccount` here is read from a successful
  // response, not guessed: it is the only field that call proves.
  const markAccountCreated = useCallback(() => {
    setStatus((prev) => ({
      ...(prev || {}),
      hasAccount: true,
      // Unmapped, so the account axis reads "Unknown" rather than borrowing
      // the pre-start NOT_STARTED. The identity footer falls back to '—' for
      // an id we were never told.
      connectStatus: undefined,
      requirements: null,
    }));
  }, []);

  const loadStatus = useCallback(async () => {
    if (!companyId) {
      // The parent gates this whole block behind its own initialLoading, so
      // an absent id here means its fetch failed. This routes into the same
      // "we don't know" render as a failed read — never the ordinary
      // NOT_STARTED invitation — and leaves Retry able to do something once
      // the parent has a real id to hand down.
      const message = 'This company could not be identified — reload the page';
      notify('error', message);
      setStatus(null);
      setLoadError(message);
      setInitialLoading(false);
      return;
    }
    setInitialLoading(true);
    try {
      const res = await connectApi.getStatus(companyId);
      const data = connectApi.toConnectStatus(res);
      if (!data) {
        // `toConnectStatus` returns null when the body it was handed is not a
        // status object — a plausible way for a 2xx to say nothing usable came
        // back, which api.js's interceptor (and so this try/catch) only
        // rejects on HTTP status, not on this. Left to fall through, `status`
        // stays null and `loadError` stays '', and the render below reads
        // that as `hasAccount === false`: "Not started" above an *enabled*
        // Start onboarding button, for a company this call never actually
        // confirmed has no account. Route it into the same "we don't know"
        // branch as a failed read instead — connect-onboarding.js and
        // companySlice.js already treat this same null as unknown, not as
        // NOT_STARTED.
        const message = "Couldn't read the Stripe status — the response was empty";
        notify('error', message);
        setStatus(null);
        setLoadError(message);
        return;
      }
      applyStatus(data);
      setLoadError('');
    } catch (error) {
      const reason = apiMessage(error);
      notify('error', reason || 'Could not read the Stripe account status');
      setStatus(null);
      setLoadError(
        reason
          ? `Couldn't read the Stripe status — ${reason}`
          : "Couldn't read the Stripe status",
      );
    } finally {
      setInitialLoading(false);
    }
  }, [companyId, notify, applyStatus]);

  const loadReadiness = useCallback(async () => {
    if (!companyId) return;
    try {
      const next = connectApi.toReadiness(await connectApi.getReadiness(companyId));
      if (!next) throw new Error('empty');
      setReadiness(next);
      setReadinessError('');
    } catch (error) {
      // Unknown is not blocked: leave `readiness` null and say the check could
      // not be run. An empty blocker list would read as nothing being wrong.
      setReadiness(null);
      setReadinessError(
        apiMessage(error)
          ? `Couldn't run the readiness check — ${apiMessage(error)}`
          : "Couldn't run the readiness check",
      );
    } finally {
      setReadinessLoaded(true);
    }
  }, [companyId]);

  const loadTerminals = useCallback(async () => {
    if (!companyId) return;
    try {
      setTerminals(connectApi.toTerminals(await connectApi.getTerminals(companyId)));
      setTerminalsError('');
    } catch (error) {
      // Leave `terminals` exactly as it was — NOT cleared to []. An empty
      // list here would make ConnectTerminals render its own "this company
      // has no terminals" sentence, which can be actively false: readiness
      // can be reporting the `terminal` blocker (real, unprepared terminals
      // exist) at the very same moment this read fails. Not worth blocking
      // the tab over either way — just say the read failed.
      setTerminalsError(
        apiMessage(error)
          ? `Couldn't read the terminals — ${apiMessage(error)}`
          : "Couldn't read the terminals",
      );
    } finally {
      setTerminalsLoaded(true);
    }
  }, [companyId]);

  const loadActivationLog = useCallback(async () => {
    if (!companyId) return;
    try {
      setActivationLog(
        connectApi.toActivationLog(await connectApi.getActivationLog(companyId)),
      );
      setActivationLogError('');
    } catch (error) {
      // Leave `activationLog` exactly as it was — not worth blocking the tab
      // over, but a silent failure must not just make the whole history
      // section disappear with no explanation either.
      setActivationLogError(
        apiMessage(error)
          ? `Couldn't read the activation history — ${apiMessage(error)}`
          : "Couldn't read the activation history",
      );
    }
  }, [companyId]);

  useEffect(() => {
    loadStatus();
    loadReadiness();
    loadTerminals();
    loadActivationLog();
    // Only a change of companyId should retrigger the initial read.
    // `loadStatus` closes over `notify`, whose identity is only stable
    // because the parent passes a raw useState setter as `setNotf` — an
    // inline `setNotf={...}` in some future parent would otherwise turn this
    // into a render -> fetch -> render loop. Retry calls loadStatus directly
    // from a click, so — like the three action handlers below — it needs no
    // unmount guard of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const onStartOnboarding = async () => {
    if (busy) return;
    setBusy('start');
    try {
      const res = await connectApi.startOnboarding(companyId);
      // StartOnboarding returns the same flat ConnectAccountStatusDto GetStatus
      // and Refresh do — just with onboardingurl (and the client secret)
      // actually populated instead of null. One settled shape, so a parse
      // that succeeds already carries everything this handler needs and
      // there is no separate "did the link come back nested" question left
      // to answer; the re-read below exists purely for the case where the
      // parse itself fails.
      const next = connectApi.toConnectStatus(res);
      if (applyStatus(next)) {
        // `applyStatus` already cleared `linkUrl`, so this only sets it back
        // when this response actually minted one.
        if (next.onboardingUrl) setLinkUrl(next.onboardingUrl);
        // A brand-new account changes what readiness and terminal prep can
        // even mean for this company — both were last read against "no
        // account at all". Re-read them the same way onPrepare re-reads
        // readiness after a change that invalidates it.
        await Promise.all([loadReadiness(), loadTerminals()]);
        notify(
          'success',
          'Onboarding started. The restaurant can finish verification from their own Settings page.',
        );
        return;
      }
      // StartOnboarding's own request already succeeded (its catch below did
      // not fire) but the body could not be read as a status object, so
      // nothing about it — including any link — is known. The account exists
      // regardless: a failure of this re-read is a stale screen, not a failed
      // start, so it must not be reported as "could not start onboarding" and
      // must not invite a second POST against an account that now exists.
      try {
        const refreshed = await connectApi.getStatus(companyId);
        if (!applyStatus(connectApi.toConnectStatus(refreshed))) {
          // The re-read told us nothing either, so treat it exactly as the
          // thrown case below.
          markAccountCreated();
          notify(
            'error',
            'Onboarding started, but the latest status could not be read — press Refresh status',
          );
          return;
        }
      } catch (refreshError) {
        markAccountCreated();
        notifyError(
          refreshError,
          'Onboarding started, but the latest status could not be read — press Refresh status',
        );
        return;
      }
      // Same reasoning as the direct-parse success path above: the account is
      // new, so what readiness/terminals last read is stale.
      await Promise.all([loadReadiness(), loadTerminals()]);
      notify(
        'success',
        'Onboarding started. The restaurant can finish verification from their own Settings page.',
      );
    } catch (error) {
      notifyError(error, 'Could not start onboarding');
    } finally {
      setBusy('');
    }
  };

  // ResendLink mints a fresh Account Link. It goes to the clipboard so the
  // admin can send it to the restaurant — it is never opened here, and it is
  // never a dashboard.stripe.com URL.
  //
  // The clipboard write happens after an await, so the click's transient user
  // activation is gone by the time it runs: Safari wants the write in the same
  // task as the gesture and Firefox wants live activation, and both reject with
  // NotAllowedError. Stripe has already minted a single-use link by then, so
  // that rejection must not be reported as "could not create a link" and must
  // not throw the URL away — it goes into state first, and the two failures say
  // different things. An absent navigator.clipboard throws a TypeError on the
  // same line and lands in the same branch, so there is no pre-flight check.
  const onCopyLink = async () => {
    if (busy) return;
    setBusy('copy');
    try {
      const res = await connectApi.resendLink(companyId);
      // ResendLink returns the same flat status object StartOnboarding does,
      // with a freshly minted onboardingUrl.
      const url = connectApi.toConnectStatus(res)?.onboardingUrl;
      if (!url) {
        // Nothing was minted — any link already on screen is for a request
        // that did not happen and must not linger under this error.
        setLinkUrl('');
        notify('error', 'Stripe did not return an onboarding link');
        return;
      }
      setLinkUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        notify('success', 'Onboarding link copied to the clipboard');
      } catch {
        notify(
          'error',
          'Could not copy automatically — the link is shown below',
        );
      }
    } catch (error) {
      notifyError(error, 'Could not create an onboarding link');
    } finally {
      setBusy('');
    }
  };

  const onRefresh = async () => {
    if (busy) return;
    setBusy('refresh');
    try {
      const res = await connectApi.refreshStatus(companyId);
      if (!applyStatus(connectApi.toConnectStatus(res))) {
        // Nothing came back, so nothing is known that wasn't known before.
        // Keep the last good facts on screen and say the refresh failed,
        // rather than reporting success over an unchanged panel.
        notify('error', 'Could not refresh the status from Stripe');
        return;
      }
      // Refresh exists to pull the Stripe facts behind the `not_ready`,
      // `charges`, `payouts` and `fee_payer` blockers — connect-readiness.js's
      // own copy tells the admin to "refresh the status if it looks stale".
      // Re-read readiness too, or following that hint repaints the account
      // axis while the blocker list underneath never moves.
      await loadReadiness();
      notify('success', 'Status refreshed from Stripe');
    } catch (error) {
      notifyError(error, 'Could not refresh the status from Stripe');
    } finally {
      setBusy('');
    }
  };

  const onPrepare = async (terminalId) => {
    if (busyTerminalId) return;
    setBusyTerminalId(terminalId);
    setTerminalErrors((prev) => ({ ...prev, [terminalId]: '' }));
    try {
      await connectApi.prepareTerminal(terminalId);
      // Preparing changes the `terminal` blocker, so readiness is now stale.
      await Promise.all([loadTerminals(), loadReadiness()]);
    } catch (error) {
      setTerminalErrors((prev) => ({
        ...prev,
        [terminalId]: apiMessage(error) || 'Stripe refused this terminal',
      }));
    } finally {
      setBusyTerminalId('');
    }
  };

  const onPrepareAll = async () => {
    // `onPrepareAll` is called with no arguments — the terminals component has
    // no way to know which rows are already prepared, so enforcing that is
    // entirely this handler's job. Re-preparing an already-prepared terminal
    // would be a pointless real Stripe call.
    for (const terminal of terminals.filter((t) => !t.prepared)) {
      // Sequential on purpose: each call creates a Stripe Location, and a burst
      // of them buys nothing but a rate limit.
      // eslint-disable-next-line no-await-in-loop
      await onPrepare(terminal.terminalId);
    }
  };

  const onActivate = async () => {
    if (busy) return;
    setBusy('activate');
    try {
      const next = connectApi.toReadiness(await connectApi.activate(companyId));
      if (!next) {
        // Activate answered 2xx, but the body carried neither `readiness`
        // nor `snapshot`, so `toReadiness` could not read it — the same
        // "nothing usable came back" case onRefresh treats as unknown rather
        // than success (api.js's interceptor only rejects on HTTP status,
        // not on this). Falling through to the success toast below would
        // claim "Direct charges are active" while the axes line stayed on
        // Legacy and Activate stayed enabled — inviting a second real POST.
        notify(
          'error',
          'Activate ran, but the response could not be read — check the status above before trying again',
        );
        return;
      }
      setReadiness(next);
      // This IS a successful readiness read by another name — Activate's
      // response just carried it instead of a dedicated GET. Without
      // clearing it, a readiness read that failed earlier leaves
      // `readinessError` set, and the load-error branch below outranks this
      // fresher list: the section would keep rendering "couldn't check"
      // instead of the blockers this response was just handed.
      setReadinessError('');
      // The routing axis renders from `status`, not from `readiness`. The
      // activate response carries the new payment model, so move both
      // consumers of that fact together — otherwise the axes line still says
      // "Legacy" after a successful activation, with no refetch to correct it.
      // Computed once from the status on screen, set, and reported to the
      // page (pre-flight I1): the company page's badge and Account Activity
      // tab read this same object.
      const activated = status ? { ...status, paymentModel: next.paymentModel } : status;
      setStatus(activated);
      if (activated && onStatusChange) onStatusChange(activated);
      notify('success', 'Direct charges are active for this company');
      await loadActivationLog();
    } catch (error) {
      const blockers = error?.response?.data?.blockers;
      // Not `Array.isArray(blockers)` alone — a refusal body of
      // `{ blockers: [], message: … }` is a legal, empty-but-valid array, and
      // writing it through cleared `readinessError` and rendered an empty
      // list where the last real explanation was standing, while the toast
      // below still points the admin at "the blockers below" — a list that
      // is no longer there. `.length > 0` leaves whatever was on screen
      // before this response untouched whenever the server sent nothing to
      // replace it with.
      if (Array.isArray(blockers) && blockers.length > 0) {
        // The server re-checked and refused. Its list is fresher than ours.
        setReadiness((prev) => ({ ...(prev || {}), ready: false, blockers }));
        // Same reasoning as the success path above: this refusal body is a
        // fresh readiness read too, so it must not be shadowed by a stale
        // `readinessError` from an earlier failed GET.
        setReadinessError('');
        // ONE message, not two: notify() and notifyError() both write the
        // same setNotf slot, so calling both here means the second silently
        // overwrites the first and the admin never learns why the blocker
        // list below just changed under them — the exact thing this branch
        // exists to prevent. Fold both facts into the single toast the slot
        // can actually hold.
        notify(
          'error',
          `${apiMessage(error) || 'Could not activate direct charges'} — the blockers below are the server's fresher check`,
        );
      } else {
        notifyError(error, 'Could not activate direct charges');
      }
    } finally {
      setBusy('');
    }
  };

  const onDeactivate = async (reason) => {
    if (busy) return;
    setBusy('deactivate');
    try {
      const next = connectApi.toReadiness(await connectApi.deactivate(companyId, reason));
      if (!next) {
        // Same "2xx but unreadable body" case as onActivate above.
        notify(
          'error',
          'Deactivate ran, but the response could not be read — check the status above before trying again',
        );
        return;
      }
      setReadiness(next);
      // Same reasoning as onActivate's own success path: this IS a
      // successful readiness read by another name.
      setReadinessError('');
      // Same as onActivate's success path: once, set, reported.
      const deactivated = status ? { ...status, paymentModel: next.paymentModel } : status;
      setStatus(deactivated);
      if (deactivated && onStatusChange) onStatusChange(deactivated);
      notify('success', 'This company is back on the platform account');
      await loadActivationLog();
    } catch (error) {
      // Mirrors onActivate's own catch above. Deactivate answers a refusal
      // the same shape Activate does — `{ blockers, message }` — and raises
      // `already_legacy` whenever the profile is not CONNECT_DIRECT, which
      // happens whenever a second admin's Deactivate lands after the first
      // one already succeeded. Without this, that admin got a raw "Connect
      // readiness failed" toast and a screen still claiming Direct routing.
      const blockers = error?.response?.data?.blockers;
      // Same reasoning as onActivate's own catch above — `.length > 0`, not a
      // bare `Array.isArray`, so an empty `blockers: []` refusal body does
      // not blank the existing explanation out from under the toast.
      if (Array.isArray(blockers) && blockers.length > 0) {
        setReadiness((prev) => ({ ...(prev || {}), ready: false, blockers }));
        setReadinessError('');
        notify(
          'error',
          `${apiMessage(error) || 'Could not deactivate direct charges'} — the blockers below are the server's fresher check`,
        );
        if (blockers.includes('already_legacy')) {
          // The server has just told us routing is already legacy — the axes
          // line must stop claiming Direct too, not just the blocker list
          // below it. A direct getStatus + applyStatus, not loadStatus():
          // loadStatus toggles `initialLoading` back to true, which would
          // swap this whole panel — the fresher blockers list just written
          // above included — back to the full loading skeleton mid-operation.
          // Best-effort, like onRefresh's own read above: if this secondary
          // read fails, routing just stays stale until the next natural one.
          try {
            applyStatus(connectApi.toConnectStatus(await connectApi.getStatus(companyId)));
          } catch {
            // Swallowed on purpose — see the comment above.
          }
        }
        return;
      }
      notifyError(error, 'Could not deactivate direct charges');
    } finally {
      setBusy('');
    }
  };

  // Re-creating changes more than a readiness snapshot can carry — a new
  // account id, onboarding state, cleared terminal locations, a log entry —
  // so unlike activate/deactivate this reloads every section rather than
  // trusting one response body. `applyStatus` first, so the new id is on
  // screen even if one of the reloads fails.
  const onRecreate = async () => {
    if (busy) return;
    setBusy('recreate');
    try {
      const next = connectApi.toConnectStatus(await connectApi.recreateAccount(companyId));
      if (!next) {
        notify(
          'error',
          'Re-create ran, but the response could not be read — refresh the status before trying again',
        );
        return;
      }
      applyStatus(next);
      notify('success', 'A new Stripe account was created — the restaurant has to finish onboarding again');
      await Promise.all([loadReadiness(), loadTerminals(), loadActivationLog()]);
    } catch (error) {
      notifyError(error, 'Could not re-create the Stripe account');
    } finally {
      setBusy('');
    }
  };

  if (initialLoading) {
    return (
      <div
        role="status"
        aria-label="Loading the Stripe account status"
        className="mt-6 rounded-md border border-slate-200 bg-white px-6 py-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <SkeletonBlock className="h-3 w-28" />
            <SkeletonBlock className="h-5 w-40" />
            <SkeletonBlock className="h-3 w-56" />
          </div>
          <div className="space-y-2">
            <SkeletonBlock className="h-3 w-28" />
            <SkeletonBlock className="h-5 w-24" />
            <SkeletonBlock className="h-3 w-48" />
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <SkeletonBlock className="h-9 w-32" />
          <SkeletonBlock className="h-9 w-32" />
        </div>
      </div>
    );
  }

  // The read failed (or there was never a companyId to read with). Render
  // neither the invitation nor the actions — both would assert a specific
  // Stripe fact ("no account yet") this screen does not actually know, and
  // the invitation's Start button would let an admin POST a second Connect
  // account into existence for a company that may already have one.
  if (loadError) {
    return (
      <div className="mt-6">
        <div className="rounded-md border border-slate-200 bg-white px-6 py-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <AxisFacts
            accountDotClass={UNKNOWN_TONE.dot}
            accountTextClass={UNKNOWN_TONE.text}
            accountLabel={UNKNOWN_TONE.label}
            accountDescription={loadError}
            routingLabel={UNKNOWN_ROUTING_LABEL}
            routingDescription={UNKNOWN_ROUTING_DESCRIPTION}
          />
          <div className="mt-6">
            <TWButton
              variant="primary"
              icon={ArrowPathIcon}
              label="Retry"
              onClick={() => {
                // All four loaders, not just loadStatus — a successful Retry
                // that restores the axes but leaves terminals empty and the
                // activation history hidden is only half a retry.
                loadStatus();
                loadReadiness();
                loadTerminals();
                loadActivationLog();
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  const hasAccount = !!status?.hasAccount;
  const tone = toneFor(status?.connectStatus);
  const routing = routingFor(status?.paymentModel);
  const requirements = status?.requirements;
  const groups = groupRequirements(requirements);

  // READY must never read as live. Routing is still legacy until Activate, so
  // the status line says so itself and no success banner is drawn anywhere.
  //
  // Keyed on routing NOT being confirmed Direct, rather than on it being
  // confirmed Legacy. The two agree on every value the adapter can produce
  // today, but they differ on an unrecognised one, and there the second form
  // silently drops the qualifier and shows a bare "Ready" — losing the caution
  // exactly when we know least. Withholding the qualifier is the claim that
  // needs evidence; keeping it does not.
  const readyButNotLive =
    status?.connectStatus === 'READY' && routing !== ROUTING.CONNECT_DIRECT;

  const statusLabel = readyButNotLive ? 'Ready — not live yet' : tone.label;
  // A company born Pending takes no card payments at all — "the old way"
  // falsely implies it is already charging through Thisway's platform
  // account the way a legacy company does. Legacy wording is unchanged.
  const statusDescription = readyButNotLive
    ? routing === ROUTING.CONNECT_PENDING
      ? 'Verified by Stripe. This company takes no card payments until an admin activates direct charges.'
      : 'Verified by Stripe. Payments still settle the old way until an admin activates direct charges.'
    : tone.description;

  // Stripe not listing anything outstanding is only trustworthy when a
  // requirements object actually came back, or the status itself (READY /
  // PENDING) makes an empty list unsurprising. A RESTRICTED account whose
  // payload simply carried no requirements is the opposite of "nothing
  // outstanding" and must not say so.
  const nothingOutstandingConfirmed =
    groups.length === 0 &&
    (!!requirements ||
      status?.connectStatus === 'READY' ||
      status?.connectStatus === 'PENDING');

  return (
    <div className="mt-6">
      <div className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white px-6 shadow-sm dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800">
        {/* Two axes, then the actions that move them */}
        <section className="py-6">
          <AxisFacts
            accountDotClass={tone.dot}
            accountTextClass={tone.text}
            accountLabel={statusLabel}
            accountDescription={statusDescription}
            routingLabel={routing.label}
            routingDescription={routing.description}
          />

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {!hasAccount ? (
              <TWButton
                variant="primary"
                icon={PaperAirplaneIcon}
                label="Start onboarding"
                disabled={!!busy || !companyId}
                loading={busy === 'start'}
                onClick={onStartOnboarding}
              />
            ) : (
              <>
                <TWButton
                  variant="default"
                  icon={ClipboardDocumentIcon}
                  label="Copy link"
                  disabled={!!busy}
                  loading={busy === 'copy'}
                  onClick={onCopyLink}
                />
                <TWButton
                  variant="default"
                  icon={ArrowPathIcon}
                  label="Refresh status"
                  disabled={!!busy}
                  loading={busy === 'refresh'}
                  onClick={onRefresh}
                />
              </>
            )}
          </div>

          {linkUrl && (
            <div className="mt-4 max-w-2xl">
              <Label>Onboarding link</Label>
              {/* Text, never an <a href>. This is a connect.stripe.com Account
                  Link, and an anchor here is one careless refactor away from
                  becoming the dashboard.stripe.com link the board forbids. */}
              <p className="mt-1 select-all break-all font-mono text-xs text-slate-700 dark:text-slate-200">
                {linkUrl}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Send this to the restaurant. Account Links are single-use and
                expire quickly — copy a fresh one if it goes stale.
              </p>
            </div>
          )}

          {!hasAccount && (
            <p className="mt-4 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
              This company has no Connect account yet. Once you start
              onboarding, the restaurant can finish verification from their own
              Settings page.
            </p>
          )}
        </section>

        <ConnectActivation
          readiness={readiness}
          isActive={status?.paymentModel === 'CONNECT_DIRECT'}
          loadError={readinessError}
          loaded={readinessLoaded}
          onRetry={loadReadiness}
          busy={busy}
          hasAccount={hasAccount}
          onActivate={onActivate}
          onDeactivate={onDeactivate}
          onRecreate={onRecreate}
        />

        <ConnectSettlement companyId={companyId} hasAccount={hasAccount} />

        {/* Support's editor of the payout schedule; the restaurant edits the
            same setting itself since 2026-10-20 (Overview, Payouts, the
            Manager app). Collapsed, and drawn only once Stripe has finished
            checking the account, like Support below. */}
        <ConnectPayoutSchedule
          companyId={companyId}
          hasAccount={hasAccount}
          isReady={status?.connectStatus === 'READY'}
          onNotify={notify}
        />

        {/* ConnectSupport calls useAccountSession/useConnectInstance itself,
            so the boundary has to wrap it from out here — a boundary inside
            that component's own render can never catch a throw from hooks
            that component calls before it returns. See
            connect-support-boundary.js's own comment. */}
        <ConnectSupportBoundary retryLabel="Retry">
          <ConnectSupport
            companyId={companyId}
            hasAccount={hasAccount}
            isReady={status?.connectStatus === 'READY'}
          />
        </ConnectSupportBoundary>

        {/* ConnectTerminals owns its own "unknown" story now — see its own
            comments: `error` renders as a line above the list whenever it is
            set (not only when the list is also empty), and `loaded` tells it
            apart "not read yet" from "confirmed zero terminals". */}
        <ConnectTerminals
          terminals={terminals}
          hasAccount={hasAccount}
          busyTerminalId={busyTerminalId}
          errors={terminalErrors}
          error={terminalsError}
          loaded={terminalsLoaded}
          onPrepare={onPrepare}
          onPrepareAll={onPrepareAll}
        />

        {hasAccount && (
          <>
            {/* Stripe's requirements — not Thisway's readiness checks. SC-R5
                adds an "Activation blockers" section below this one; the two
                lists must never merge. */}
            <section className="py-6">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                What the restaurant still has to do
              </h3>

              {groups.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  {nothingOutstandingConfirmed
                    ? 'Nothing outstanding. Stripe is not waiting on anything from this restaurant.'
                    : "Stripe hasn't told us what, if anything, is still needed from this restaurant."}
                </p>
              ) : (
                <div className="mt-4 space-y-5">
                  {groups.map((group) => (
                    <div key={group.key}>
                      <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                        {group.title}
                      </p>
                      <ul className="mt-2 space-y-1.5">
                        {group.items.map((item) => (
                          <li
                            key={item.code}
                            className="flex flex-col gap-0.5 text-sm sm:flex-row sm:gap-4"
                          >
                            <span className="text-slate-900 dark:text-slate-100 sm:w-72 sm:shrink-0">
                              {item.label}
                            </span>
                            {item.hint && (
                              <span className="text-slate-500 dark:text-slate-400">
                                {item.hint}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Quiet identity footer. The acct_ id is display-and-copy only —
                the browser never sends it back as authoritative. No Created /
                Last refreshed rows: the shipped DTO carries neither
                timestamp, and a blank or "Invalid Date" row is worse than no
                row at all. */}
            <section className="py-6">
              <dl className="space-y-2 text-sm">
                <div className="flex flex-wrap gap-x-4">
                  <dt className="w-32 shrink-0 text-slate-500 dark:text-slate-400">
                    Account
                  </dt>
                  <dd className="font-mono text-slate-700 dark:text-slate-200">
                    {status?.stripeAccountId || '—'}
                  </dd>
                </div>
              </dl>
            </section>
          </>
        )}

        {/* Renders on either condition, not just a non-empty log — a failed
            read must not just make the whole section vanish with nothing
            said, the same "unknown is not blocked" rule the terminals
            section above follows. */}
        {(activationLog.length > 0 || activationLogError) && (
          <section className="py-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Activation history
            </h3>
            {activationLogError && (
              <p className="mt-3 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                {activationLogError}
              </p>
            )}
            {activationLog.length > 0 && (
              <ul className="mt-3 space-y-2">
                {activationLog.map((entry) => {
                  // Guarded the same way the identity footer's own comment
                  // above insists on: a blank or "Invalid Date" row is worse
                  // than no date at all, so an unparseable `createdAt` drops
                  // the date suffix rather than rendering "Invalid Date".
                  const createdAt = entry.createdAt ? new Date(entry.createdAt) : null;
                  const createdAtValid = !!createdAt && !Number.isNaN(createdAt.getTime());

                  return (
                    <li key={entry.id} className="text-sm text-slate-600 dark:text-slate-300">
                      <span className="text-slate-900 dark:text-slate-100">
                        {humanizeActivationAction(entry.action)}
                      </span>
                      {/* Onboarding rows move the account-status axis and route
                          nothing, so they arrive with fromModel === toModel. An arrow
                          between two identical values reads as a routing change that
                          failed. */}
                      {entry.fromModel !== entry.toModel
                        ? ` · ${routingFor(entry.fromModel).label} → ${routingFor(entry.toModel).label}`
                        : ''}
                      {/* `reason` is human text ONLY on deactivations. Activate writes
                          the constant "admin_activate" and onboarding writes
                          "start_onboarding" — machine codes, not sentences someone
                          typed. Rendering them unconditionally puts "admin_activate"
                          in front of an admin as if it were an explanation. */}
                      {entry.action === 'DEACTIVATE' && entry.reason ? ` · ${entry.reason}` : ''}
                      {entry.actorAdminId ? ` · ${entry.actorAdminId}` : ''}
                      {createdAtValid ? ` · ${createdAt.toLocaleString()}` : ''}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default ConnectTab;
